import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { SlotInfo } from 'react-big-calendar';
import { useSelector } from 'react-redux';
import {
  useGetAppointmentApi,
  useGetAppointmentsApi,
  useUpdateAppointmentApi,
  useCreateAppointmentTypeApi,
  useGetAppointmentTypesApi,
} from '../../api';
import { GetAppointmentParams } from '../../types';
import { Appointment, AppointmentType } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { getRandomHexColor } from 'src/modules/settings/helpers';
//import moment from "moment";
import Calendar from './Calendar';
import { CreateAppointmentModal } from '../CreateAppointmentModal';
import '../CreateAppointmentModal/styles.scss';
import { DownloadCsvModal } from '../DownloadCsvModal';
import './index.scss';

const components = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: (props: any) => {
    return (
      <Box
        bgcolor={props.event.data.appointment.appointmentType.color}
        borderRadius={1}
        pt={0.5}
        pb={0.25}
        px={0.625}
        typography="caption"
        height="100%"
      >
        {props.event.data.appointment.name}
      </Box>
    );
  },
};

type AppointmentCalendarProps = {
  selectMode?: boolean;
  onSelect?: (appointment: Appointment) => void;
};

export default function AppointmentCalendar({
  selectMode = false,
  onSelect,
}: AppointmentCalendarProps = {}) {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const getAppointments = useGetAppointmentsApi();
  const getAppointment = useGetAppointmentApi();
  const updateAppointment = useUpdateAppointmentApi();
  const createDefaultAppointmentType = useCreateAppointmentTypeApi();
  const getAppointmentTypes = useGetAppointmentTypesApi();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDownloadCsvModal, setShowDownloadCsvModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>();
  const [filterAppointmentTypeId, setFilterAppointmentTypeId] = useState<string>("All");
  const [startDate, setStartDate] = useState<string>(dayjs().startOf("week").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<string>(dayjs().startOf("week").add(7, "d").format("YYYY-MM-DD"));

  const [slotStartDate, setSlotStartDate] = useState<string>();
  const [slotEndDate, setSlotEndDate] = useState<string>();

  const initialAppointment: Appointment = {
    name: "",
    startDate: startDate,
    endDate: endDate,
    companyId: selectedCompanyId!,
  };

  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const onRangeChange = (
    range:
      | Date[]
      | {
          start: Date;
          end: Date;
        }
  ) => {
    if (Array.isArray(range)) {
      setStartDate(dayjs(range[0]).format("YYYY-MM-DD"));
      setEndDate(
        dayjs(range[range.length - 1])
          .add(1, "d")
          .format("YYYY-MM-DD")
      );
    } else if (typeof range === "object" && range !== null) {
      setStartDate(dayjs(range.start).format("YYYY-MM-DD"));
      setEndDate(dayjs(range.end).add(1, "d").format("YYYY-MM-DD"));
    }
  };

  const onSelectSlot = (slotInfo: SlotInfo) => {
    if (selectMode) {
      return;
    }
    setSelectedAppointment(null);
    setSlotStartDate(dayjs(slotInfo.start).toISOString());
    setSlotEndDate(dayjs(slotInfo.end).toISOString());
    setShowCreateModal(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loadAppointments = useCallback(() => {
    if (!appointmentTypes || !selectedCompanyId) {
      return;
    }
    const typeIds = appointmentTypes.map((t) => t.id);

    if (!typeIds.length) {
      return;
    }

    const params: GetAppointmentParams = {
      companyId: selectedCompanyId,
      appointmentTypeIds: filterAppointmentTypeId !== "All" ? filterAppointmentTypeId : typeIds.join(","),
      statuses: "active",
      startDateInterval: startDate!,
      endDateInterval: endDate!,
      limit: 100000,
      offset: 0,
    };
    getAppointments(params).then((response) => {
      setAppointments(response.data.items);
    });
  }, [appointmentTypes, selectedCompanyId, filterAppointmentTypeId, startDate, endDate, getAppointments]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelectEvent = (event: any) => {
    if (selectMode) {
      if (event?.data?.appointment) {
        onSelect?.(event.data.appointment);
      }
      return;
    }
    if (event.data.appointment.id) {
      getAppointment(event.data.appointment.id, selectedCompanyId!).then((response) => {
        setSelectedAppointment(response.data);
        setShowCreateModal(true);
      });
    }
  };

  const onEventDrop = useCallback(({ event, start, end }: {
    event: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    start: Date;
    end: Date;
  }) => {
    const appointment = event.data.appointment;
    if (appointment?.id && selectedCompanyId) {
      // Optimistically update the local state immediately to prevent jumping
      setAppointments(prevAppointments => 
        prevAppointments.map(apt => 
          apt.id === appointment.id 
            ? { ...apt, startDate: dayjs(start).toISOString(), endDate: dayjs(end).toISOString() }
            : apt
        )
      );

      // Update the appointment with new start and end dates
      updateAppointment(
        appointment.id,
        appointment.name,
        appointment.appointmentType.id,
        dayjs(start).toISOString(),
        dayjs(end).toISOString(),
        selectedCompanyId,
        appointment.description,
        appointment.location
      ).then(() => {
        // Refresh the calendar after successful update to sync with server
        loadAppointments();
      }).catch((error) => {
        console.error('Failed to update appointment:', error);
        // Revert the optimistic update on error
        loadAppointments();
      });
    }
  }, [updateAppointment, selectedCompanyId, loadAppointments]);

  const onEventResize = useCallback(({ event, start, end }: {
    event: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    start: Date;
    end: Date;
  }) => {
    const appointment = event.data.appointment;
    if (appointment?.id && selectedCompanyId) {
      // Optimistically update the local state immediately to prevent jumping
      setAppointments(prevAppointments => 
        prevAppointments.map(apt => 
          apt.id === appointment.id 
            ? { ...apt, startDate: dayjs(start).toISOString(), endDate: dayjs(end).toISOString() }
            : apt
        )
      );

      // Update the appointment with new start and end dates
      updateAppointment(
        appointment.id,
        appointment.name,
        appointment.appointmentType.id,
        dayjs(start).toISOString(),
        dayjs(end).toISOString(),
        selectedCompanyId,
        appointment.description,
        appointment.location
      ).then(() => {
        // Refresh the calendar after successful update to sync with server
        loadAppointments();
      }).catch((error) => {
        console.error('Failed to resize appointment:', error);
        // Revert the optimistic update on error
        loadAppointments();
      });
    }
  }, [updateAppointment, selectedCompanyId, loadAppointments]);

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }

    const typesParams = {
      limit: 10000,
      offset: 0,
      companyId: selectedCompanyId,
      statuses: "active,archived",
    };
    getAppointmentTypes(typesParams).then((responseTypes) => {
      if (responseTypes.data.count === 0) {
        createDefaultAppointmentType("Default", getRandomHexColor(), selectedCompanyId).then((response) => {
          setAppointmentTypes([response.data]);
        });
      } else {
        setAppointmentTypes(responseTypes.data.items);
      }
    });
  }, [selectedCompanyId, selectMode]);

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    loadAppointments();
  }, [selectedCompanyId, startDate, endDate, appointmentTypes, filterAppointmentTypeId]);

  const getEvents = (appointments: Appointment[]) => {
    return appointments.map((a) => {
      return {
        start: new Date(a.startDate!),
        end: new Date(a.endDate!),
        data: { appointment: a },
      };
    });
  };

  const handleChangeFilterAppointmentType = ({ target }: SelectChangeEvent<string>) => {
    const type = appointmentTypes?.find((a) => a.id === target.value);
    if (type) {
      setFilterAppointmentTypeId(type.id);
    } else {
      setFilterAppointmentTypeId("All");
    }
  };

  return (
    <>
      {appointmentTypes && (
        <Stack direction="row" mt={2.5} justifyContent="space-between" alignItems="center">
          <Box>
            <FormControl sx={{ width: 192 }}>
              <InputLabel>Calendar</InputLabel>
              <Select
                inputProps={{}}
                size="small"
                sx={{ mb: 0.5, minWidth: "100%" }}
                label="Calendar"
                MenuProps={{
                  disableScrollLock: true,
                }}
                value={filterAppointmentTypeId}
                onChange={handleChangeFilterAppointmentType}
                name="fieldName"
              >
                <MenuItem value={"All"} key={"all"}>
                  All
                </MenuItem>
                {appointmentTypes
                  .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                  .map((c) => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.name} {c.status === "active" ? "" : "(archived)"}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          {!selectMode && (
            <Stack direction="row" spacing={2.5} mt={-2.5}>
              <Button
                size="small"
                variant="contained"
                sx={{ width: 127 }}
                onClick={() => {
                  setShowDownloadCsvModal(true);
                }}
              >
                Import
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{ width: 127 }}
                onClick={() => {
                  setSelectedAppointment(null);
                  setSlotStartDate(undefined);
                  setSlotEndDate(undefined);
                  setShowCreateModal(true);
                }}
              >
                Create
              </Button>
            </Stack>
          )}
        </Stack>
      )}
      <Calendar
        onSelectSlot={onSelectSlot}
        onSelectEvent={onSelectEvent}
        onEventDrop={onEventDrop}
        onEventResize={onEventResize}
        onRangeChange={onRangeChange}
        defaultView="week"
        selectable={!selectMode}
        scrollToTime={selectMode ? new Date(0, 0, 0, 0, 0, 0) : new Date(0, 0, 0, 6, 0, 0)}
        views={["month", "week", "day", "agenda"]}
        events={getEvents(appointments)}
        components={components}
      />
      {showCreateModal && (
        <CreateAppointmentModal
          slotStartDate={slotStartDate}
          slotEndDate={slotEndDate}
          appointmentTypes={appointmentTypes!}
          show={showCreateModal}
          setShowModal={setShowCreateModal}
          appointment={selectedAppointment}
          initialAppointment={initialAppointment}
          refreshAppointments={loadAppointments}
        />
      )}
      {showDownloadCsvModal && (
        <DownloadCsvModal
          show={showDownloadCsvModal}
          setShowModal={setShowDownloadCsvModal}
          refreshAppointments={loadAppointments}
        />
      )}
    </>
  );
}
